import { STORAGE_KEYS } from '@/app/config/constants';

/**
 * The `organizationCode` half of the login bootstrap
 * (organization-setup-step1-apiflow.md section 2.1).
 *
 * Three states, not two. The document is explicit that a null organizationCode
 * must still be written, because "the absence of a stored key should never be
 * confused with 'not checked yet'":
 *
 *   key absent      -> login bootstrap has not run
 *   key === ''      -> checked, and the user has no organization yet -> POST
 *   key === 'ORG-1' -> checked, organization exists                  -> PUT
 *
 * The empty string encodes null because localStorage stores strings only and
 * `getItem` already returns null for an absent key.
 *
 * Lives in shared/ rather than in the feature so the auth layer can write it
 * at login without importing organization types.
 */

const NULL_SENTINEL = '';

function store(): Storage | null {
  try {
    const probe = '__niraxobid_org_probe__';
    window.localStorage.setItem(probe, probe);
    window.localStorage.removeItem(probe);
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Records what GET /users/me reported. Call with null too -- that is the point
 * of the sentinel.
 */
export function setStoredOrganizationCode(code: string | null): void {
  try {
    store()?.setItem(STORAGE_KEYS.ORGANIZATION_CODE, code ?? NULL_SENTINEL);
  } catch {
    /* storage unavailable -- the wizard falls back to create-then-refresh */
  }
}

/** The organization's code, or null when the user has none yet. */
export function getStoredOrganizationCode(): string | null {
  try {
    const raw = store()?.getItem(STORAGE_KEYS.ORGANIZATION_CODE) ?? null;
    return raw === null || raw === NULL_SENTINEL ? null : raw;
  } catch {
    return null;
  }
}

/**
 * Whether the login bootstrap has run at all. Distinct from
 * `getStoredOrganizationCode() === null`, which is also true for a user who
 * genuinely has no organization.
 */
export function hasCheckedOrganizationCode(): boolean {
  try {
    return store()?.getItem(STORAGE_KEYS.ORGANIZATION_CODE) !== null;
  } catch {
    return false;
  }
}

export function clearStoredOrganizationCode(): void {
  try {
    store()?.removeItem(STORAGE_KEYS.ORGANIZATION_CODE);
  } catch {
    /* nothing to do */
  }
}
